<template>
  <div>
    <div class="space-y-4">
      <!-- Page header -->
      <div class="flex items-center gap-3">
        <span class="flex items-center justify-center w-10 h-10 rounded-xl bg-taxiye-100 text-taxiye-500">
          <span class="material-icons-round">request_quote</span>
        </span>
        <div>
          <h2 class="text-lg font-semibold text-gray-800">Loan History</h2>
          <p class="text-sm text-gray-500">Track driver loans and repayments.</p>
        </div>
      </div>

      <div class="p-4 bg-white border border-gray-200 rounded-2xl shadow-sm">
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center space-x-1 text-gray-500">
            <span class="text-base material-icons-round">filter_list</span>
            <span class="text-xs font-semibold tracking-widest uppercase">Filters</span>
          </div>
        </div>

        <div class="flex flex-wrap items-end gap-4">
          <!-- From driver -->
          <div class="relative w-full sm:w-56">
            <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">From</label>
            <div class="relative mt-1.5">
              <span class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <span class="text-base text-gray-500 material-icons-round">search</span>
              </span>
              <input
                v-model="selectedDriverFrom"
                @keyup="searchDriverFrom"
                class="w-full h-10 pl-9 pr-3 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="Search drivers..."
              />
            </div>
            <transition
              enter-active-class="transition duration-100 ease-out"
              enter-class="transform scale-95 opacity-0"
              enter-to-class="transform scale-100 opacity-100"
              leave-active-class="transition duration-75 ease-in"
              leave-class="transform scale-100 opacity-100"
              leave-to-class="transform scale-95 opacity-0"
            >
              <div
                v-if="fromSearchResult.length"
                class="absolute inset-x-0 z-50 mt-2"
              >
                <div
                  class="py-1 overflow-y-auto bg-white border border-gray-100 shadow-lg rounded-xl max-h-64"
                >
                  <ul>
                    <li
                      v-for="(result, index) in fromSearchResult"
                      :key="result._id"
                      @click="selectDriverFrom(index)"
                      class="flex items-center px-3 py-2 space-x-3 cursor-pointer transition-colors duration-150 hover:bg-taxiye-50"
                    >
                      <img
                        class="object-cover w-10 h-10 rounded-full"
                        :src="result.profileImage"
                        :alt="`${result.firstName} ${result.lastName}`"
                      />
                      <div>
                        <p class="text-sm font-semibold text-gray-800">
                          {{ `${result.firstName} ${result.lastName}` }}
                        </p>
                        <p class="text-xs text-gray-500">
                          {{ result.phoneNumber }}
                        </p>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </transition>
          </div>

          <!-- To driver -->
          <div class="relative w-full sm:w-56">
            <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">To</label>
            <div class="relative mt-1.5">
              <span class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <span class="text-base text-gray-500 material-icons-round">search</span>
              </span>
              <input
                v-model="selectedDriverTo"
                @keyup="searchDriverTo"
                class="w-full h-10 pl-9 pr-3 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="Search drivers..."
              />
            </div>
            <transition
              enter-active-class="transition duration-100 ease-out"
              enter-class="transform scale-95 opacity-0"
              enter-to-class="transform scale-100 opacity-100"
              leave-active-class="transition duration-75 ease-in"
              leave-class="transform scale-100 opacity-100"
              leave-to-class="transform scale-95 opacity-0"
            >
              <div
                v-if="toSearchResult.length"
                class="absolute inset-x-0 z-50 mt-2"
              >
                <div
                  class="py-1 overflow-y-auto bg-white border border-gray-100 shadow-lg rounded-xl max-h-64"
                >
                  <ul>
                    <li
                      v-for="(result, index) in toSearchResult"
                      :key="result._id"
                      @click="selectDriverTo(index)"
                      class="flex items-center px-3 py-2 space-x-3 cursor-pointer transition-colors duration-150 hover:bg-taxiye-50"
                    >
                      <img
                        class="object-cover w-10 h-10 rounded-full"
                        :src="result.profileImage"
                        :alt="`${result.firstName} ${result.lastName}`"
                      />
                      <div>
                        <p class="text-sm font-semibold text-gray-800">
                          {{ `${result.firstName} ${result.lastName}` }}
                        </p>
                        <p class="text-xs text-gray-500">
                          {{ result.phoneNumber }}
                        </p>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </transition>
          </div>

          <!-- Status -->
          <div class="w-full sm:w-48">
            <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Status</label>
            <select
              class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-select rounded-xl focus:border-taxiye-300"
              v-model="paid"
              @change="getAllLoans"
            >
              <option :value="null">All</option>
              <option :value="true">Paid</option>
              <option :value="false">Unpaid</option>
            </select>
          </div>

          <!-- Date range -->
          <div class="w-full sm:w-auto">
            <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Date Range</label>
            <div class="flex items-center h-10 mt-1.5 bg-white border border-gray-300 shadow-sm rounded-xl">
              <span class="pl-3 pr-1 text-base text-gray-500 material-icons-round">event</span>
              <v-date-picker v-model="range" is-range>
                <template v-slot="{ inputValue, inputEvents }">
                  <div class="flex items-center">
                    <input
                      :value="inputValue.start"
                      v-on="inputEvents.start"
                      placeholder="From"
                      class="w-24 h-8 px-2 text-sm text-gray-800 bg-transparent rounded-lg focus:outline-none focus:bg-taxiye-50"
                    />
                    <span class="mx-1 text-base text-gray-300 material-icons-round">arrow_forward</span>
                    <input
                      :value="inputValue.end"
                      v-on="inputEvents.end"
                      placeholder="To"
                      class="w-24 h-8 px-2 text-sm text-gray-800 bg-transparent rounded-lg focus:outline-none focus:bg-taxiye-50"
                    />
                  </div>
                </template>
              </v-date-picker>
              <button
                v-if="range.start || range.end"
                @click="range = { start: null, end: null }"
                type="button"
                title="Clear dates"
                class="flex items-center justify-center w-8 h-8 mx-1 text-gray-400 rounded-lg transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 focus:outline-none"
              >
                <span class="text-lg material-icons-round">close</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-4 overflow-hidden bg-white border border-gray-200 rounded-xl shadow-sm">
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead>
              <tr class="bg-gray-100 border-b border-gray-200">
                <th
                  v-for="col in [
                    'Loan ID',
                    'From',
                    'To',
                    'Amount',
                    'Status',
                    'Last Transaction Date',
                  ]"
                  :key="`col-${col}`"
                  class="px-5 py-3 text-xs font-semibold tracking-wider text-left text-gray-500 uppercase whitespace-no-wrap"
                >
                  {{ col }}
                </th>
              </tr>
            </thead>
            <tbody class="relative" v-if="loans.length">
              <tr
                v-for="(loan, index) in loans"
                :key="loan._id"
                class="transition-colors duration-150 hover:bg-taxiye-50"
                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-100'"
              >
                <!-- Loan ID -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <span class="font-mono text-xs text-gray-500">{{ loan._id }}</span>
                </td>
                <!-- From -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <div class="flex items-center space-x-3">
                    <span
                      class="flex items-center justify-center flex-shrink-0 w-10 h-10 text-xs font-bold rounded-full"
                      :class="avatarClass(personLabel(loan.from))"
                    >{{ initials(personLabel(loan.from)) }}</span>
                    <div>
                      <p class="font-medium text-gray-900">
                        {{ personLabel(loan.from, "Unknown") }}
                      </p>
                      <p class="text-xs text-gray-500">
                        {{ loan.from ? loan.from.phoneNumber || loan.from._id : "—" }}
                      </p>
                    </div>
                  </div>
                </td>
                <!-- To -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <div class="flex items-center space-x-3">
                    <span
                      class="flex items-center justify-center flex-shrink-0 w-10 h-10 text-xs font-bold rounded-full"
                      :class="avatarClass(personLabel(loan.to))"
                    >{{ initials(personLabel(loan.to)) }}</span>
                    <div>
                      <p class="font-medium text-gray-900">
                        {{ personLabel(loan.to, "Unknown") }}
                      </p>
                      <p class="text-xs text-gray-500">
                        {{ loan.to ? loan.to.phoneNumber || loan.to._id : "—" }}
                      </p>
                    </div>
                  </div>
                </td>
                <!-- Amount -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <p class="font-medium text-gray-900">{{ loan.amount.toFixed(2) }}</p>
                </td>
                <!-- Paid status -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <span
                    class="inline-flex items-center px-2 py-1 text-xs font-semibold leading-none rounded-full"
                    :class="loan.paid ? 'text-green-700 bg-green-100' : 'text-red-700 bg-red-100'"
                  >
                    <span
                      class="w-2 h-2 mr-2 rounded-full"
                      :class="loan.paid ? 'bg-green-500' : 'bg-red-500'"
                    ></span>
                    {{ loan.paid ? "Paid" : "Unpaid" }}
                  </span>
                </td>
                <!-- Last transaction date -->
                <td class="px-5 py-4 text-gray-600 whitespace-no-wrap">
                  <div class="flex items-center space-x-2">
                    <i class="text-sm text-gray-400 material-icons-round">schedule</i>
                    <span>{{ formatDate(loan.updatedAt) || "—" }}</span>
                  </div>
                </td>
              </tr>
              <div
                v-if="loading"
                class="absolute inset-0 flex items-center justify-center w-full h-full bg-white bg-opacity-50"
              >
                <i class="text-5xl text-taxiye-500 material-icons-round spin">sync</i>
                <span class="invisible block">Loading...</span>
              </div>
            </tbody>
            <tbody v-else>
              <tr>
                <td colspan="6" class="py-16 text-center bg-white">
                  <div v-if="loading" class="inline-block">
                    <i class="text-5xl text-taxiye-500 material-icons-round spin">sync</i>
                    <span class="invisible block">Loading...</span>
                  </div>
                  <div v-else class="flex flex-col items-center text-gray-400">
                    <span class="flex items-center justify-center w-16 h-16 mb-3 rounded-full bg-gray-100">
                      <i class="text-3xl material-icons-round">inbox</i>
                    </span>
                    <span class="text-sm font-medium text-gray-500">No loans found</span>
                    <span class="mt-1 text-xs text-gray-400">Try adjusting your filters</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="flex items-end justify-between">
        <pagination
          class="flex space-x-2"
          :currentPage="currentPage"
          :total="total"
          @goto="goToPage"
          @setPerPage="setPerPage"
          :perPage="perPage"
          :start="start"
          :end="end"
          :count="paginator.count"
        ></pagination>
      </div>
    </div>
  </div>
</template>

<script>
import PaginationMixin from "@/mixins/Pagination";
import Pagination from "@/components/Pagination";

export default {
  mixins: [PaginationMixin],
  data() {
    return {
      loading: false,
      paid: null,
      loans: [],
      selectedDriverFrom: "",
      selectedDriverTo: "",
      drivers: [],
      fromSearchResult: [],
      toSearchResult: [],
      fromId: null,
      toId: null,
      range: {
        start: null,
        end: null,
      },
    };
  },
  components: {
    Pagination,
  },
  watch: {
    range: {
      handler() {
        this.getAllLoans();
      },
      deep: true,
    },
    currentPage() {
      this.getAllLoans();
    },
    perPage() {
      this.getAllLoans();
    },
  },
  created() {
    var sd = new Date();
    sd.setDate(0);
    sd.setDate(1);
    var ed = new Date();
    ed.setMonth(ed.getMonth() + 1);
    ed.setDate(0);
    this.startDate = sd;
    this.endDate = ed;
    this.getAllLoans();
    this.$misc
      .get("/drivers")
      .then((response) => {
        this.drivers = response.data.data;
        let driver = this.drivers.find((driver) => driver._id == this.driverId);
        if (driver) {
          this.selectedDriverFrom = `${driver.firstName} ${driver.lastName} - ${driver.phoneNumber}`;
        }
      })
      .catch((err) => console.log(err));
  },
  methods: {
    getAllLoans() {
      this.loading = true;
      var path = `/loan-histories?populate=["from","to"]&page=${this.currentPage}&limit=${this.perPage}`;
      if (this.fromId) {
        path += "&from=" + this.fromId;
      }
      if (this.toId) {
        path += "&to=" + this.toId;
      }
      if (this.paid && this.paid != "all") {
        path += "&paid=" + this.paid;
      }
      if (this.range.start) {
        path += "&start=" + this.range.start;
      }
      if (this.range.end) {
        path += "&end=" + this.range.end;
      }
      this.$misc
        .get(path)
        .then((response) => {
          this.loans = response.data.data;
          this.paginator = {
            nextPage: response.data.nextPage,
            prevPage: response.data.prevPage,
            count: response.data.count,
          };
        })
        .catch((err) => console.log(err))
        .finally(() => (this.loading = false));
    },
    searchDriverFrom(e) {
      if (e.target.value.trim() === "") {
        this.fromSearchResult = [];
        this.fromId = null;
        this.getAllLoans();
      } else if (!isNaN(parseInt(e.target.value))) {
        this.fromSearchResult = this.drivers.filter((driver) => {
          return driver.phoneNumber.slice(-4).includes(e.target.value);
        });
      } else {
        this.fromSearchResult = this.drivers.filter((driver) => {
          let name = driver.firstName + " " + driver.lastName;
          return name.toLowerCase().includes(e.target.value.toLowerCase());
        });
      }
    },
    searchDriverTo(e) {
      if (e.target.value.trim() === "") {
        this.toSearchResult = [];
        this.toId = null;
        this.getAllLoans();
      } else if (!isNaN(parseInt(e.target.value))) {
        this.toSearchResult = this.drivers.filter((driver) => {
          return driver.phoneNumber.slice(-4).includes(e.target.value);
        });
      } else {
        this.toSearchResult = this.drivers.filter((driver) => {
          let name = driver.firstName + " " + driver.lastName;
          return name.toLowerCase().includes(e.target.value.toLowerCase());
        });
      }
    },
    selectDriverFrom(index) {
      let driver = this.fromSearchResult[index];
      this.selectedDriverFrom = `${driver.firstName} ${driver.lastName} - ${driver.phoneNumber}`;
      this.fromId = driver._id;
      this.fromSearchResult = [];
      this.getAllLoans();
    },
    selectDriverTo(index) {
      let driver = this.toSearchResult[index];
      this.selectedDriverTo = `${driver.firstName} ${driver.lastName} - ${driver.phoneNumber}`;
      this.toId = driver._id;
      this.toSearchResult = [];
      this.getAllLoans();
    },
    formatDate(date) {
      return date ? date.replace("T", " ").slice(0, 16) : "";
    },
    personLabel(person, fallback = "") {
      if (!person) return fallback;
      const name = `${person.firstName || ""} ${person.lastName || ""}`.trim();
      if (!name) return fallback;
      return name
        .split(" ")
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
    },
    initials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .filter(Boolean)
        .map((part) => part[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
    },
    avatarClass(name) {
      const palette = [
        "bg-taxiye-100 text-taxiye-600",
        "bg-indigo-50 text-indigo-600",
        "bg-blue-50 text-blue-600",
        "bg-green-50 text-green-600",
        "bg-purple-50 text-purple-600",
        "bg-pink-50 text-pink-600",
      ];
      let hash = 0;
      for (let i = 0; i < (name || "").length; i++) {
        hash = (hash + name.charCodeAt(i)) % palette.length;
      }
      return palette[hash];
    },
  },
};
</script>
